/**
 * Created by piotr.pozniak@thebeaverhead.com on 11/04/2023
 */

import React, { useCallback, useContext, useMemo } from "react";
import classNames from "classnames";
import RuleEditDropdown from "./RuleEditDropdown";
import { AvailableRulesOptionsContext, FiltersContext } from "./contexts";
import RuleLabel from "./RuleLabel";

/**
 * @typedef {Object} Field
 * @property {string} label - The display label of the field.
 * @property {string} value - The internal value of the field.
 */

/**
 * @typedef {Object} Operator
 * @property {string} label - The display label of the operator.
 * @property {string} value - The internal value of the operator.
 */

/**
 * @typedef {Object} RuleData
 * @property {Field} field - The field used in the rule.
 * @property {Operator} operator - The operator used in the rule.
 * @property {string} value - The value to compare against.
 * @property {string} label - The display label of the rule.
 */

/**
 *
 * @param {number} groupId
 * @param {Number} id
 * @param {RuleData} rule
 * @param {function} _onRemoveRule
 * @returns {React.JSX.Element}
 * @constructor
 */
const Rule = ({ groupId, id, rule, onRemoveRule: _onRemoveRule }) => {
  const { newRule, clearNewRule } = useContext(FiltersContext);

  const isNewRule = useMemo(
    () => newRule && newRule.id === id && newRule.groupId === groupId,
    [newRule]
  );

  const [isEditMode, setIsEditMode] = React.useState(isNewRule);

  const availableRulesOptions = useContext(AvailableRulesOptionsContext);

  const optionSettings = useMemo(
    () =>
      availableRulesOptions.find(
        (option) => option.field.value === rule.field.value
      ),
    [availableRulesOptions, rule.field.value]
  );

  /**
   *
   * @type {(function(): void)|*}
   */
  const setEditMode = useCallback(() => {
    setIsEditMode(true);
  }, [isEditMode]);

  /**
   *
   * @type {(function(*): void)|*}
   */
  const onRemoveRule = useCallback(
    (e) => {
      // stop propagating otherwise it would try to setEditMode
      e && e.stopPropagation();
      _onRemoveRule(id);
    },
    [_onRemoveRule, id]
  );

  /**
   *
   * @type {(function(): void)|*}
   */
  const onCloseEditDropdown = useCallback(
    (e) => {
      // stop propagating otherwise it would try to setEditMode
      e && e.stopPropagation();
      setIsEditMode(false);

      if (isNewRule) {
        clearNewRule();
      }
    },
    [clearNewRule, isNewRule]
  );

  const editDropdown = isEditMode ? (
    <RuleEditDropdown
      groupId={groupId}
      id={id}
      onClose={onCloseEditDropdown}
      rule={rule}
    />
  ) : null;

  const hasError = !isEditMode && (!rule.value || !rule.value.length);

  const displayValue = !hasError
    ? `${rule.operator.label} ${rule.label}`
    : "is missing value";

  return (
    <div
      onClick={setEditMode}
      className={classNames("filter-group__rule", {
        "filter-group__rule-error": hasError,
        "filter-group__rule-active": isEditMode,
      })}
    >
      <span>
        <strong>
          <RuleLabel rule={rule} filter={optionSettings} />
        </strong>{" "}
        {displayValue}
      </span>
      <div onClick={onRemoveRule} className={"filter-group__rule--remove"}>
        &times;
      </div>
      {editDropdown}
    </div>
  );
};

export default Rule;
